import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from 'react-router-dom';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { FSDoc } from 'react-rainbow-firebase';
import { Spinner, RainbowThemeContainer, HelpText } from 'react-rainbow-components';
import RenderIf from 'react-rainbow-components/components/RenderIf';
import { showFormModal } from '../../../../../redux/actions/form-modal';
import { getFullPatientName, parsePhoneNumbers, shouldShowLegacyPhone } from '../../../../../helpers';
import RecordCard from '../../../../../components/record-card';
import RecordColumnSection from '../../../../../components/record-column-section';
import Output from '../../../../../components/output';
import DateTime from '../../../../../components/date-time';
import ProspectForm from '../../prospect-form';
import Timeline from '../../../patients/patient-details/details/timeline';
import '../../../patients/patient-details/details/styles.css';
import PhoneNumbers from '../../../../../components/phone-numbers';

function DateTimeOutput({ value }) {
    if (value) {
        return <DateTime value={value.toDate()} />;
    }
    return null;
}

DateTimeOutput.propTypes = {
    value: PropTypes.object,
};

DateTimeOutput.defaultProps = {
    value: undefined,
};

const theme = {
    rainbow: {
        palette: {
            warning: '#a77940',
        },
    },
};

function Details(props) {
    const {
        doc,
        isLoading,
        showFormModal,
        updateDoc,
    } = props;

    if (doc && doc.data && doc.data.enable) {
        const {
            id,
            data: {
                firstName,
                lastName,
                phone,
                phoneNumbers,
                dob,
                medicaid,
                medicare,
                medicalInsurance,
                address,
                createdAt,
                createdBy,
                notes,
            },
        } = doc;
        const displayName = createdBy && createdBy.displayName;
        const email = createdBy && createdBy.email;
        const historyRef = `prospects/${id}/history`;

        const openEditFormModal = () => {
            showFormModal({
                title: 'Edit Prospect',
                formComponent: ProspectForm,
                formId: 'prospect-form',
                onSubmit: updateDoc,
                initialValues: {
                    ...doc.data,
                    phoneNumbers: parsePhoneNumbers(doc.data.phoneNumbers || undefined),
                },
            });
        };

        const getDob = () => {
            if (dob) {
                return <FormattedDate value={dob} />;
            }
            return <span className="scheduler-patient-details_output-undefined-value">Date of birth not defined</span>;
        };

        const showLegacyPhone = shouldShowLegacyPhone(phone, phoneNumbers);

        return (
            <article className="scheduler-patient-details_content">
                <RecordCard
                    className="rainbow-m-bottom_medium"
                    title="Prospect Details"
                    onEdit={openEditFormModal}
                >
                    <div className="rainbow-flex">
                        <RecordColumnSection>
                            <Output
                                className="rainbow-m-bottom_medium"
                                label="Prospect Id.">
                                {id}
                            </Output>
                            <Output
                                className="rainbow-m-bottom_medium"
                                label="Name">
                                {getFullPatientName(firstName, lastName)}
                            </Output>
                            <RenderIf isTrue={showLegacyPhone}>
                                <Output
                                    className="rainbow-m-bottom_medium"
                                    label={(
                                        <RainbowThemeContainer theme={theme}>
                                            <div className="scheduler-patients_patient-detail-invalid-phone-label">
                                                <HelpText
                                                    className="rainbow-m-right_x-small"
                                                    variant="warning"
                                                    iconSize="small"
                                                    title={<FormattedMessage id="patients.phone.number.invalid.help.message.title" defaultMessage="The phone number need to be updated." />}
                                                    text={<FormattedMessage id="patients.phone.number.invalid.help.message" defaultMessage="We have detected that this phone number is invalid, you must edit the patient information to correctly enter the phone number(s)." />}
                                                />
                                                <FormattedMessage id="patients.phone.number.invalid" defaultMessage="Invalid Phone Number" />
                                            </div>
                                        </RainbowThemeContainer>
                                    )}>
                                    {phone}
                                </Output>
                            </RenderIf>
                            <Output
                                className="rainbow-m-bottom_medium"
                                label="Phone Numbers">
                                <PhoneNumbers value={phoneNumbers} />
                            </Output>
                            <Output
                                className="rainbow-m-bottom_medium"
                                label="Date of Birth">
                                {getDob()}
                            </Output>
                            <Output
                                className="scheduler-patient-details_card-output-large"
                                label="Address">
                                {address}
                            </Output>
                        </RecordColumnSection>
                        <RecordColumnSection>
                            <Output
                                className="rainbow-m-bottom_medium"
                                label="Medicaid">
                                {medicaid || '-'}
                            </Output>
                            <Output
                                className="rainbow-m-bottom_medium"
                                label="Medicare">
                                {medicare}
                            </Output>
                            <Output
                                className="rainbow-m-bottom_medium"
                                label="Medical Insurance">
                                {medicalInsurance}
                            </Output>
                            <Output
                                className="rainbow-m-bottom_medium"
                                label="Created At">
                                <DateTimeOutput value={createdAt} />
                            </Output>
                            <Output
                                className="rainbow-m-bottom_medium"
                                label="Created By">
                                <RenderIf isTrue={!!displayName}>
                                    {`${displayName} • `}
                                </RenderIf>
                                {email}
                            </Output>
                        </RecordColumnSection>
                    </div>
                    <Output
                        className="rainbow-m-bottom_medium rainbow-p-horizontal_medium"
                        label="Notes">
                        {notes}
                    </Output>
                </RecordCard>
                <Timeline historyRef={historyRef} />
            </article>
        );
    }
    if (isLoading) {
        return (
            <div className="scheduler-patient-details_content">
                <Spinner size="large" />
            </div>
        );
    }
    return (
        <div className="scheduler-patient-details_content scheduler-appointment_not-found-container">
            <div className="scheduler-appointment_not-found-text">
                <h1 className="scheduler-appointment_not-found-title">It looks like you’re lost…</h1>
                <Link className="scheduler-appointment_not-found-link" to="/app/prospects">Go to Prospects Page</Link>
            </div>
        </div>
    );
}

Details.propTypes = {
    doc: PropTypes.object,
    isLoading: PropTypes.bool.isRequired,
    updateDoc: PropTypes.func.isRequired,
    showFormModal: PropTypes.func.isRequired,
};

Details.defaultProps = {
    doc: null,
};

function DetailsWrapper(props) {
    const {
        match: { params: { id } },
        showFormModal,
    } = props;

    return (
        <FSDoc
            docRef={`prospects/${id}`}
            component={Details}
            showFormModal={showFormModal} />
    );
}

DetailsWrapper.propTypes = {
    match: PropTypes.object.isRequired,
    showFormModal: PropTypes.func.isRequired,
};

function stateToProps() {
    return {};
}

function dispatchToProps(dispatch) {
    return bindActionCreators({
        showFormModal,
    }, dispatch);
}

export default connect(stateToProps, dispatchToProps)(DetailsWrapper);
